import { CURRENCYCONFIG } from 'BreetHelpers';
import { memo, useCallback } from 'react';
import type { TooltipPayload, TooltipProps } from 'recharts';
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import type { Payload } from 'recharts/types/component/DefaultTooltipContent';

import { buildChartData, formatYAxisTick, type IAnalyticsChart, rawChartDataObject } from './Analytics.helpers';

export const AnalyticsChart = memo(
	({ dataLoading, vipStatsGraphData, activeTab, activePeriod, activeCurrency }: IAnalyticsChart) => {
		const rawChartData = rawChartDataObject(vipStatsGraphData)[activeTab] ?? [];

		const chartData = buildChartData(rawChartData, activePeriod);

		const currencySymbol = CURRENCYCONFIG[activeCurrency].symbol;

		type TooltipFormatter = NonNullable<TooltipProps['formatter']>;

		const tooltipFormatter = useCallback<TooltipFormatter>(
			(value) => [`${currencySymbol}${formatYAxisTick(Number(value))}`, activeTab],
			[activeTab, currencySymbol]
		);

		const labelFormatter = useCallback(
			(_: unknown, payload: TooltipPayload | readonly Payload[]) =>
				(payload[0]?.payload as { tooltipLabel?: string }).tooltipLabel ?? '',
			[]
		);

		return (
			<div className='appanalytics_chartcontainer'>
				{dataLoading ? (
					<div className='appanalytics_chartloader'>
						<div className='button-spinner' />
					</div>
				) : (
					<ResponsiveContainer
						width='100%'
						height={280}
					>
						<AreaChart
							data={chartData}
							margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
						>
							<defs>
								<linearGradient
									id='analyticsGradient'
									x1='0'
									y1='0'
									x2='0'
									y2='1'
								>
									<stop
										offset='5%'
										stopColor='var(--dark-turquoise)'
										stopOpacity={0.15}
									/>
									<stop
										offset='95%'
										stopColor='var(--dark-turquoise)'
										stopOpacity={0}
									/>
								</linearGradient>
							</defs>
							<CartesianGrid
								strokeDasharray=''
								vertical={false}
								stroke='var(--color-neutral-300)'
							/>
							<XAxis
								dataKey='label'
								axisLine={false}
								tickLine={false}
								tick={{ fontSize: 11, fill: 'var(--color-primary-300)', fontFamily: 'Campton' }}
							/>
							<YAxis
								axisLine={false}
								tickLine={false}
								tickFormatter={formatYAxisTick}
								tick={{ fontSize: 11, fill: 'var(--color-primary-300)', fontFamily: 'Campton' }}
								width={45}
							/>
							<Tooltip
								contentStyle={{
									background: 'var(--color-neutral-50)',
									border: '1px solid var(--color-neutral-300)',
									borderRadius: '0.8rem',
									fontSize: '1.2rem',
									fontFamily: 'Campton',
								}}
								formatter={tooltipFormatter}
								labelFormatter={labelFormatter}
								labelStyle={{ color: 'var(--color-primary-300)' }}
							/>
							<Area
								type='monotone'
								dataKey='value'
								stroke='var(--dark-turquoise)'
								strokeWidth={2}
								fill='url(#analyticsGradient)'
								dot={false}
								activeDot={{ r: 4, fill: 'var(--dark-turquoise)' }}
							/>
						</AreaChart>
					</ResponsiveContainer>
				)}
			</div>
		);
	}
);

AnalyticsChart.displayName = 'AnalyticsChart';
